/**
 * How far a paste reaches.
 *
 * Copying one cell and selecting many is how a value gets spread over a column
 * in Excel and in Sheets, so it has to be how it works here: the clipboard block
 * repeats over the selection. It repeats only when the selection is a whole
 * number of blocks in each direction — a 2-row block over a 3-row selection has
 * no sensible reading, and both of those applications paste it once rather than
 * guessing. A block larger than the selection also pastes once, spilling past
 * it, which is again what they do.
 */
export interface PasteExtent {
  /** Rows to write, counted from the selection's first row. */
  rows: number;
  /** Columns to write, counted from the selection's first column. */
  cols: number;
  /** Whether the block is being repeated rather than pasted once. */
  repeats: boolean;
}

export function pasteExtent(
  block: { rows: number; cols: number },
  selection: { rows: number; cols: number },
): PasteExtent {
  const repeats = selection.rows >= block.rows
    && selection.cols >= block.cols
    && selection.rows % block.rows === 0
    && selection.cols % block.cols === 0;

  return repeats
    ? { rows: selection.rows, cols: selection.cols, repeats }
    : { rows: block.rows, cols: block.cols, repeats };
}
